/* @flow */
import invariant from 'invariant';
import { Map } from 'immutable';

type FilterKey = string;

export type Filter = {
  key: FilterKey,
  name: string,
  eventElementName?: string,
};

export type Filters = Map<FilterKey, Filter>;

export type Table = {
  sort?: string,
  filters: Filters,
  query: string,
};

export type State = Map<string, Table>;
export const initialState: State = new Map();

export default function reducer(state: State = initialState, action: *): State {
  switch (action.type) {
    case 'tables/INIT_TABLE': {
      return state.set(action.namespace, { query: '', filters: new Map(), ...action.options });
    }

    case 'tables/SET_COLUMN_SORT': {
      let table;

      table = state.get(action.namespace);
      table = { ...table, sort: action.sort };

      return state.set(action.namespace, table);
    }

    case 'tables/ADD_FILTER': {
      const table = state.get(action.namespace);
      invariant(table, 'tables/ADD_FILTER missing table');

      return state.set(action.namespace, {
        ...table,
        filters: table.filters.set(action.filter.key, action.filter),
      });
    }

    case 'tables/REMOVE_FILTER': {
      const table = state.get(action.namespace);
      invariant(table, 'tables/REMOVE_FILTER missing table');

      return state.set(action.namespace, {
        ...table,
        filters: table.filters.delete(action.filter.key),
      });
    }

    case 'tables/FLUSH_FILTERS': {
      const table = state.get(action.namespace);
      invariant(table, 'tables/FLUSH_FILTERS missing table');

      return state.set(action.namespace, {
        ...table,
        filters: new Map(),
      });
    }

    case 'tables/SET_QUERY': {
      let table;

      table = state.get(action.namespace);
      table = { ...table, query: action.query };

      return state.set(action.namespace, table);
    }

    default:
      return state;
  }
}

type InitTableAction = {
  type: 'tables/INIT_TABLE',
  namespace: string,
  options?: {
    sort?: string,
  },
};

export function initTable(namespace: string, options?: { sort?: string }): InitTableAction {
  return {
    type: 'tables/INIT_TABLE',
    namespace,
    options,
  };
}

type SetQueryAction = {
  type: 'tables/SET_QUERY',
  namespace: string,
  query: string,
};

export function setQuery(namespace: string, query: string): SetQueryAction {
  return {
    type: 'tables/SET_QUERY',
    namespace,
    query,
  };
}

type AddFilterAction = {
  type: 'tables/ADD_FILTER',
  namespace: string,
  filter: Filter,
};

export function addFilter(namespace: string, filter: Filter): AddFilterAction {
  return {
    type: 'tables/ADD_FILTER',
    namespace,
    filter,
  };
}

type RemoveFilterAction = {
  type: 'tables/REMOVE_FILTER',
  namespace: string,
  filter: Filter,
};

export function removeFilter(namespace: string, filter: Filter): RemoveFilterAction {
  return {
    type: 'tables/REMOVE_FILTER',
    namespace,
    filter,
  };
}

type FlushFiltersAction = {
  type: 'tables/FLUSH_FILTERS',
  namespace: string,
};

export function flushFilters(namespace: string): FlushFiltersAction {
  return {
    type: 'tables/FLUSH_FILTERS',
    namespace,
  };
}

export type SetSortAction = {
  type: 'tables/SET_COLUMN_SORT',
  namespace: string,
  sort: string,
};

export function setSort(namespace: string, sort: string): SetSortAction {
  return {
    type: 'tables/SET_COLUMN_SORT',
    namespace,
    sort,
  };
}

export type TablesAction =
  | InitTableAction
  | SetSortAction
  | SetQueryAction
  | RemoveFilterAction
  | AddFilterAction
  | FlushFiltersAction;
